import { useState } from 'react';
import {
  Stack,
  Paper,
  Group,
  Text,
  NumberInput,
  Select,
  MultiSelect,
  Textarea,
  Button,
  Table,
  Badge,
  SimpleGrid,
  Card,
  Skeleton,
  Timeline,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconSparkles,
  IconShoppingCartPlus,
  IconCalendarEvent,
  IconClock,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import { useAIEventPlanner, useAddToCart } from '@/hooks/api';

const EVENT_TYPES = [
  { value: 'wedding', label: 'Wedding' },
  { value: 'corporate', label: 'Corporate Event' },
  { value: 'birthday', label: 'Birthday Party' },
  { value: 'holiday', label: 'Holiday Gathering' },
  { value: 'catering', label: 'Catering Job' },
  { value: 'banquet', label: 'Banquet' },
  { value: 'bbq', label: 'BBQ / Cookout' },
  { value: 'buffet', label: 'Buffet' },
  { value: 'other', label: 'Other' },
];

const DIETARY_OPTIONS = [
  'Vegetarian',
  'Vegan',
  'Gluten-Free',
  'Dairy-Free',
  'Nut-Free',
  'Kosher',
  'Halal',
  'Low-Sodium',
];

export default function EventPlanner() {
  const [eventType, setEventType] = useState<string | null>(null);
  const [guestCount, setGuestCount] = useState<number>(50);
  const [budget, setBudget] = useState<number | undefined>(undefined);
  const [dietary, setDietary] = useState<string[]>([]);
  const [preferences, setPreferences] = useState('');
  const [addingToCart, setAddingToCart] = useState(false);

  const eventPlanner = useAIEventPlanner();
  const addToCart = useAddToCart();

  const result = eventPlanner.data?.data;

  const handlePlan = () => {
    if (!eventType) {
      notifications.show({
        title: 'Missing Event Type',
        message: 'Please select an event type',
        color: 'orange',
      });
      return;
    }
    if (!guestCount || guestCount < 1) {
      notifications.show({
        title: 'Missing Guest Count',
        message: 'Please enter the number of guests',
        color: 'orange',
      });
      return;
    }
    eventPlanner.mutate({
      event_type: eventType,
      guest_count: guestCount,
      budget: budget || undefined,
      dietary_restrictions: dietary.length > 0 ? dietary : undefined,
      preferences: preferences || undefined,
    });
  };

  const handleAddAllToCart = async () => {
    if (!result?.products?.length) return;
    setAddingToCart(true);
    let successCount = 0;
    try {
      for (const item of result.products) {
        if (item.quantity > 0) {
          await addToCart.mutateAsync({
            product_id: item.product_id,
            quantity: item.quantity,
          });
          successCount++;
        }
      }
      if (successCount > 0) {
        notifications.show({
          title: 'Success',
          message: `Added ${successCount} item${successCount > 1 ? 's' : ''} to cart`,
          color: 'green',
        });
      }
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to add items to cart',
        color: 'red',
      });
    } finally {
      setAddingToCart(false);
    }
  };

  const handleAddItem = async (item: {
    product_id: number;
    product_name: string;
    quantity: number;
  }) => {
    try {
      await addToCart.mutateAsync({
        product_id: item.product_id,
        quantity: item.quantity,
      });
      notifications.show({
        title: 'Added to Cart',
        message: `${item.product_name} added to cart`,
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to add item to cart',
        color: 'red',
      });
    }
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title="Event Planner"
        description="Tell us about your event and get a full menu plan with products"
      />

      {/* Event Form */}
      <Paper p="lg" radius="md" withBorder>
        <Stack gap="md">
          <Text fw={600}>Event Details</Text>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            <Select
              label="Event Type"
              placeholder="Select event type"
              data={EVENT_TYPES}
              value={eventType}
              onChange={setEventType}
              required
            />
            <NumberInput
              label="Guest Count"
              placeholder="Number of guests"
              value={guestCount}
              onChange={(v) => setGuestCount(Number(v) || 1)}
              min={1}
              max={10000}
              required
            />
            <NumberInput
              label="Budget (optional)"
              placeholder="Total budget"
              prefix="$"
              value={budget}
              onChange={(v) => setBudget(v ? Number(v) : undefined)}
              min={0}
              decimalScale={2}
            />
            <MultiSelect
              label="Dietary Restrictions"
              placeholder="Select restrictions"
              data={DIETARY_OPTIONS}
              value={dietary}
              onChange={setDietary}
            />
          </SimpleGrid>
          <Textarea
            label="Additional Preferences"
            placeholder="e.g. heavy appetizers, Mediterranean style, outdoor event..."
            value={preferences}
            onChange={(e) => setPreferences(e.currentTarget.value)}
            minRows={2}
          />
          <Group>
            <Button
              leftSection={<IconSparkles size={16} />}
              onClick={handlePlan}
              loading={eventPlanner.isPending}
              disabled={!eventType}
            >
              Plan My Event
            </Button>
          </Group>
        </Stack>
      </Paper>

      {/* Results */}
      {eventPlanner.isPending && (
        <Stack gap="md">
          <Skeleton height={200} radius="md" />
          <Skeleton height={300} radius="md" />
        </Stack>
      )}

      {result && (
        <>
          {/* Summary Cards */}
          <SimpleGrid cols={{ base: 1, sm: 3 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="xs">
                <Text size="sm" c="dimmed" fw={500}>Total Cost</Text>
                <Text size="xl" fw={700}>${result.total_cost.toFixed(2)}</Text>
              </Stack>
            </Card>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="xs">
                <Text size="sm" c="dimmed" fw={500}>Per Guest</Text>
                <Text size="xl" fw={700}>${result.per_guest_cost.toFixed(2)}</Text>
              </Stack>
            </Card>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="xs">
                <Text size="sm" c="dimmed" fw={500}>Products Needed</Text>
                <Text size="xl" fw={700}>{result.products?.length ?? 0}</Text>
              </Stack>
            </Card>
          </SimpleGrid>

          {/* Menu Sections */}
          {result.menu_sections && result.menu_sections.length > 0 && (
            <Paper p="lg" radius="md" withBorder>
              <Stack gap="md">
                <Text fw={600} size="lg">Suggested Menu</Text>
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                  {result.menu_sections.map((section) => (
                    <Card key={section.course} padding="md" radius="md" withBorder>
                      <Stack gap="xs">
                        <Badge variant="light" size="lg">{section.course}</Badge>
                        {section.items.map((item, idx) => (
                          <div key={idx}>
                            <Text size="sm" fw={500}>{item.name}</Text>
                            {item.description && (
                              <Text size="xs" c="dimmed">{item.description}</Text>
                            )}
                          </div>
                        ))}
                      </Stack>
                    </Card>
                  ))}
                </SimpleGrid>
              </Stack>
            </Paper>
          )}

          {/* Products Table */}
          {result.products && result.products.length > 0 && (
            <Paper p="lg" radius="md" withBorder>
              <Stack gap="md">
                <Group justify="space-between">
                  <Text fw={600} size="lg">Products to Order</Text>
                  <Button
                    leftSection={<IconShoppingCartPlus size={16} />}
                    onClick={handleAddAllToCart}
                    loading={addingToCart}
                  >
                    Add All to Cart
                  </Button>
                </Group>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Product</Table.Th>
                      <Table.Th>Quantity</Table.Th>
                      <Table.Th>Unit Price</Table.Th>
                      <Table.Th>Total</Table.Th>
                      <Table.Th>Action</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {result.products.map((item) => (
                      <Table.Tr key={item.product_id}>
                        <Table.Td>
                          <div>
                            <Text fw={500} size="sm">{item.product_name}</Text>
                            {item.sku && (
                              <Text size="xs" c="dimmed">SKU: {item.sku}</Text>
                            )}
                          </div>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{item.quantity} {item.unit}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">${item.unit_price.toFixed(2)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text fw={600} size="sm">${item.line_total.toFixed(2)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Button
                            size="xs"
                            variant="light"
                            leftSection={<IconShoppingCartPlus size={14} />}
                            onClick={() => handleAddItem(item)}
                            loading={addToCart.isPending}
                          >
                            Add
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Stack>
            </Paper>
          )}

          {/* Timeline */}
          {result.timeline && result.timeline.length > 0 && (
            <Paper p="lg" radius="md" withBorder>
              <Stack gap="md">
                <Text fw={600} size="lg">Event Timeline</Text>
                <Timeline active={-1} bulletSize={24} lineWidth={2}>
                  {result.timeline.map((step, idx) => (
                    <Timeline.Item
                      key={idx}
                      bullet={<IconClock size={12} />}
                      title={step.time}
                    >
                      <Text size="sm" c="dimmed">{step.task}</Text>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Stack>
            </Paper>
          )}
        </>
      )}

      {!eventPlanner.isPending && !result && (
        <Paper p="lg" radius="md" withBorder>
          <EmptyState
            title="Plan Your Next Event"
            description="Fill in the details above and let AI create a complete menu plan with products you can order."
            icon={<IconCalendarEvent size={48} />}
          />
        </Paper>
      )}
    </Stack>
  );
}
